import React, { useState } from 'react';
import { toast } from 'react-toastify';
import type { Template } from '../../types';
import { EmojiDisplay } from '../common/EmojiPicker';
import { Button } from '../ui/button';

interface TemplateCardProps {
  template: Template;
  onDelete: (templateId: string) => void;
  onEdit: (templateId: string) => void;
}

export const TemplateCard: React.FC<TemplateCardProps> = ({ template, onDelete, onEdit }) => {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleEditClick = () => {
    onEdit(template.id);
  };

  const handleDeleteClick = () => {
    toast.warning(
      <div>
        <p className="font-semibold mb-2">Delete template "{template.name}"?</p>
        <p className="text-sm mb-2">
          This will also delete any associated Discord messages.
        </p>
        <div className="flex gap-2 mt-3">
          <button
            onClick={() => {
              setIsDeleting(true);
              toast.dismiss();
              onDelete(template.id);
            }}
            className="px-3 py-1 bg-red-600 hover:bg-red-700 rounded text-sm font-medium"
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
          <button
            onClick={() => {
              toast.dismiss();
            }}
            className="px-3 py-1 bg-gray-600 hover:bg-gray-700 rounded text-sm font-medium"
            disabled={isDeleting}
          >
            Cancel
          </button>
        </div>
      </div>,
      {
        autoClose: false,
        closeButton: false,
        draggable: false,
        closeOnClick: false,
      }
    );
  };

  return (
    <div className="bg-surface-container-low border-l-4 border-success rounded-lg overflow-hidden flex flex-col group transition-all duration-300 hover:bg-surface-container">
      <div className="p-6">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-bold text-on-surface group-hover:text-primary-text transition-colors">
              {template.name}
            </h3>
            {template.selection_mode === 'single' && (
              <span className="text-[10px] font-bold uppercase tracking-wider bg-primary/20 text-primary-text px-2 py-0.5 rounded-full">
                Single
              </span>
            )}
          </div>
        </div>
        <p className="text-on-surface-variant text-sm line-clamp-2 mb-6">
          {template.message_content}
        </p>

        {template.roles && template.roles.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {template.roles.map((role) => {
              const hexColor = `#${role.color.toString(16).padStart(6, '0')}`;
              return (
                <span
                  key={role.role_id}
                  className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full flex items-center gap-1.5 border"
                  style={{
                    backgroundColor: `${hexColor}22`,
                    color: hexColor,
                    borderColor: `${hexColor}44`,
                  }}
                >
                  {role.emoji && <EmojiDisplay emoji={role.emoji} className="w-3 h-3" />} {role.name}
                </span>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-auto p-4 bg-surface-container-high flex gap-3 justify-end">
        <Button
          variant="secondary"
          size="sm"
          onClick={handleEditClick}
          disabled={isDeleting}
        >
          <span className="material-symbols-outlined text-base">edit</span>
          Edit
        </Button>
        <Button
          variant="destructive"
          size="sm"
          onClick={handleDeleteClick}
          disabled={isDeleting}
        >
          <span className="material-symbols-outlined text-base">delete</span>
          Delete
        </Button>
      </div>
    </div>
  );
};
